import { _notNil, uuid } from '@/littledash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DeviceContext, type DevicesProviderContextResponse } from './DevicesProvider';

interface UseDevicesProps {
  targetId?: string;
  onNewReading?: (reading: string) => void;
  onDeviceError?: () => void;
  ref?: React.RefObject<HTMLElement>;
}

type UnwantedKeys =
  | 'nextReading'
  | 'readings'
  | 'clearReading'
  | 'deregisterTarget'
  | 'registerTarget'
  | 'isTargetReady'
  | 'setOnDeviceError';

interface UseDevicesResponse extends Omit<DevicesProviderContextResponse, UnwantedKeys> {
  nextReading: (complete: boolean) => void;
  searchBarTriggered: {
    targetId: string | null;
    value: string | null;
    timestamp: number;
  };
  clearSearchBarTrigger: () => void;
  applyDelayedSearchReading: (targetId: string, value: string) => void;
  setSearchInterceptCondition: (shouldIntercept: (targetId: string, value: string) => boolean) => void;
}

export const useDevices = ({ targetId, onNewReading, onDeviceError }: UseDevicesProps): UseDevicesResponse => {
  const [identifier] = useState(uuid());
  const {
    disabled,
    deviceTypes,
    mappedDevices,
    unmappedDevices,
    readyTargets,
    readings,
    activePreset,
    searchBarTriggered,
    onChangeDeviceTarget,
    onChangeDeviceType,
    clearReading,
    clearSearchBarTrigger,
    applyDelayedSearchReading,
    setSearchInterceptCondition,
    deregisterTarget,
    registerTarget,
    nextReading,
    getDevices,
    refreshDevices,
    isTargetReady,
    setOnDeviceError,
    resetDeviceOrder,
  } = useContext(DeviceContext);

  useEffect(() => {
    if (_notNil(targetId) && _notNil(mappedDevices)) {
      registerTarget(targetId, identifier);
      return () => {
        deregisterTarget(targetId, identifier);
      };
    }
  }, [mappedDevices]);

  useEffect(() => {
    if (_notNil(targetId)) {
      const isReady = isTargetReady(targetId, identifier);
      const reading = readings[targetId];
      // ignore the reading if the field is not visible or if the ref has not been set
      if (_notNil(reading) && _notNil(onNewReading)) {
        clearReading(targetId);
        if (isReady) {
          onNewReading(reading);
        } else {
          _nextReading(true);
        }
      }
    }
  }, [readings]);

  useEffect(() => {
    if (_notNil(targetId) && _notNil(setOnDeviceError) && _notNil(onDeviceError)) {
      setOnDeviceError(targetId, onDeviceError);
    }

    return () => {
      if (_notNil(targetId) && _notNil(setOnDeviceError) && _notNil(onDeviceError)) {
        setOnDeviceError(targetId, () => {});
      }
    };
  }, [setOnDeviceError]);

  const _nextReading = useCallback(
    (complete: boolean) => {
      if (_notNil(targetId)) {
        nextReading(targetId, complete);
      }
    },
    [targetId]
  );

  return {
    disabled,
    deviceTypes,
    mappedDevices,
    unmappedDevices,
    readyTargets,
    searchBarTriggered,
    onChangeDeviceTarget,
    onChangeDeviceType,
    activePreset,
    nextReading: _nextReading,
    clearSearchBarTrigger,
    applyDelayedSearchReading,
    setSearchInterceptCondition,
    getDevices,
    refreshDevices,
    resetDeviceOrder,
  };
};
